/**
 * NxInformeVertical — una respuesta de UNA sola fila se lee como informe, no como tabla.
 *
 * UIA-T8. "Como nos fue ayer" devolvia 11 columnas en una tabla con scroll horizontal:
 * negocio_nombre, ventas, gastos, creditos, neto, origen_dato, base_calculo_ventas,
 * base_calculo_gastos, facturas_recibidas, colaboradores_marcaron, periodo_label.
 * En movil no se veia ni la mitad, y `2875000.00` es ilegible al lado de `2089300.00`.
 *
 * La referencia es FrmArqueoV15 del Desktop: etiqueta arriba, cifra abajo, lo importante
 * grande. Es el lenguaje que Josse ya lee todos los dias, asi que el panel no deberia
 * inventar otro para decir lo mismo.
 *
 * GENERICO POR FORMA, no por capacidad: cualquier resultado de una sola fila con varias
 * columnas se pinta asi. Es el mismo criterio de NxResumenPorPersona, que termino
 * sirviendo para tres capacidades justamente por no conocer ninguna.
 *
 * @module presentation/shared/NxInformeVertical
 */

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from './texto.js';

// Columnas que son PLATA. Se formatean con separador de miles y signo.
// Se decide por nombre y no por magnitud: "colaboradores_marcaron: 6" es un conteo, y
// ponerle signo de pesos seria peor que no formatear nada.
const RE_MONEDA = /(venta|gasto|credito|crédito|neto|total|monto|saldo|ingreso|egreso|costo|precio|valor|utilidad|efectivo|transferencia|abono|diferencia)/i;

// ...salvo los CONTEOS, que suelen colarse por el nombre. `num_pagos` contiene "pago"
// y es una cantidad, no plata: sin esta guarda saldria como "$ 8" y seria una cifra
// inventada. La guarda va primero y manda sobre RE_MONEDA.
const RE_CONTEO = /^(num_|cantidad|conteo|cuantos)|_count$|^facturas_|marcaron$/i;
const esColumnaDePlata = (c) => !RE_CONTEO.test(c) && RE_MONEDA.test(c);

// La letra chica: dice DE DONDE sale cada cifra. Pasa a segundo plano visual, que es
// distinto de desaparecer -- ese dato costo dos dias de trabajo.
const RE_PIE = /^(base_calculo|origen_dato|periodo|.*_label|.*_id|fuente|nota)/i;

// Conceptos que encabezan el informe cuando existen, en este orden.
const DESTACADOS = ['ventas', 'gastos', 'egresos', 'ingresos', 'neto', 'utilidad', 'saldo_final'];

// UIA-T10: columnas que son DESGLOSE de otra. Se pintan indentadas debajo del concepto
// del que forman parte, en vez de sueltas como si fueran conceptos independientes.
// "Efectivo 391.000" al mismo nivel que "Ventas 690.000" invita a sumarlos; debajo y
// con sangria se lee como lo que es: una parte del de arriba.
// UIA-T13: el anidado se deduce del PREFIJO. `ventas_efectivo` y `ventas_transferencia`
// cuelgan de `ventas` sin que yo tenga que enumerarlos; `gastos_caja`, `gastos_banco` y
// `gastos_externos` cuelgan de `gastos`. Es mas robusto que una lista fija: el dia que
// la capacidad agregue `ventas_credito`, se anida solo.
// El mapa explicito queda para los desgloses que NO siguen la convencion de nombre.
const SUBRENGLONES = {
  ventas:  ['efectivo', 'transferencias', 'transferencia'],
  gastos:  ['egresos_externos'],
  egresos: ['egresos_externos'],
};

// El titulo no es un renglon. "Negocio nombre: Exito Chino" en medio de las cifras se
// lee como un dato mas del informe cuando en realidad es de quien es el informe.
const RE_TITULO = /^(negocio_nombre|business_name|nombre_negocio)$/i;

// Contexto: acompanan al informe pero no son plata del negocio. Van en su propio bloque
// al final para que no compitan con ventas/gastos/neto.
const RE_CONTEXTO = /^(diferencia_arqueo|diferencia|facturas_|colaboradores_|num_)/i;

const esNumero = (v) => typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && /^-?\d+([.,]\d+)?$/.test(v.trim()));
const aNumero = (v) => typeof v === 'number' ? v : parseFloat(String(v).replace(/\s/g, '').replace(',', '.'));

/** 2875000.00 -> $ 2.875.000 (COP no usa decimales en el dia a dia). */
function fmtMoneda(n) {
  const entero = Math.round(Math.abs(n));
  const conMiles = entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${n < 0 ? '-' : ''}$ ${conMiles}`;
}

function fmtNumero(n) {
  return Math.abs(n) >= 10000
    ? Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    : String(n);
}

function etiquetaDe(col) {
  const s = String(col).replace(/_/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** ¿Este resultado se lee mejor como informe vertical? */
export function detectarInforme(rows) {
  if (!Array.isArray(rows) || rows.length !== 1) return null;
  const fila = rows[0] || {};
  const cols = Object.keys(fila);
  // Con 3 columnas o menos la tabla ya se lee bien y no hace falta cambiar nada.
  if (cols.length < 4) return null;
  const hayNumerica = cols.some(c => !RE_PIE.test(c) && esNumero(fila[c]));
  if (!hayNumerica) return null;
  return { cols, fila };
}

/**
 * HTML del informe, o '' si el resultado no se beneficia.
 * @param {Array<object>} rows
 */
export function renderInformeVertical(rows) {
  const info = detectarInforme(rows);
  if (!info) return '';
  const { cols, fila } = info;

  // UIA-T10 -- NULL NO ES CERO, y esta distincion es el nucleo de esta entrega.
  //
  // Medido el 3-ago: el mismo dia, Exito Chino trae creditos/transferencia/
  // egresos_externos en NULL mientras Punto Rosa, Plasticos y Crocs traen valores
  // reales. En el historico, 581 de 1.130 arqueos tienen creditos > 0. O sea la
  // columna no esta muerta: hay negocios que no reportan ese campo, por origen legacy.
  //
  // Si pintara 0 estaria afirmando "no hubo creditos" cuando la verdad es "no lo
  // sabemos". Es el mismo error que dar por bueno un faltante porque la casilla estaba
  // vacia. Por eso el informe distingue TRES estados y no dos: valor, cero de verdad,
  // y sin dato -- y el sin dato se dice con palabras, no con un cero.
  const sinDato = [];
  const pie = [], destacados = [], normales = [], contexto = [];
  let titulo = null;
  for (const c of cols) {
    const v = fila[c];
    if (RE_TITULO.test(c)) { titulo = v; continue; }   // UIA-T13: es el titulo, no un renglon
    if (v === null || v === undefined || v === '') {
      // Solo se declara "sin dato" lo que es un concepto del informe. Que falte una
      // etiqueta de periodo no es una laguna que el dueno tenga que ver.
      if (!RE_PIE.test(c)) sinDato.push(c);
      continue;
    }
    if (RE_PIE.test(c)) { pie.push([c, v]); continue; }
    if (RE_CONTEXTO.test(c)) { contexto.push([c, v]); continue; }
    const idx = DESTACADOS.indexOf(String(c).toLowerCase());
    (idx >= 0 ? destacados : normales).push([c, v, idx]);
  }
  destacados.sort((a, b) => a[2] - b[2]);

  // Los desgloses salen de la lista suelta y se pintan bajo su concepto padre.
  // Dos caminos: por PREFIJO (ventas_efectivo -> ventas) y por el mapa explicito, para
  // los que no siguen la convencion de nombre.
  const hijosDe = new Map();
  const nombresDestacados = destacados.map(([c]) => String(c).toLowerCase());
  for (const padre of nombresDestacados) {
    const porPrefijo = normales.filter(([c]) => String(c).toLowerCase().startsWith(padre + '_'));
    const porMapa = (SUBRENGLONES[padre] || [])
      .map(n => normales.find(([c]) => String(c).toLowerCase() === n))
      .filter(Boolean);
    const hijos = [...new Set([...porPrefijo, ...porMapa])];
    if (hijos.length > 0) hijosDe.set(padre, hijos);
  }
  const esHijo = new Set([...hijosDe.values()].flat().map(([c]) => c));

  const lineaDestacada = ([c, v]) => {
    const num = esNumero(v) ? aNumero(v) : null;
    const esNeto = /neto|utilidad|saldo_final/i.test(c);
    // El neto con color segun signo, como el arqueo: es la cifra que se mira primero.
    const color = esNeto ? (num > 0 ? '#3fb950' : num < 0 ? '#f85149' : '#c9d1d9') : '#e6edf3';
    const texto = num === null ? esc(v) : (esColumnaDePlata(c) ? fmtMoneda(num) : fmtNumero(num));
    return `
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding:9px 0;border-bottom:1px solid #21262d">
        <span style="font-size:0.875rem;color:#8b949e">${esc(etiquetaDe(c))}</span>
        <span style="font-size:1.15rem;font-weight:700;color:${color};font-variant-numeric:tabular-nums;white-space:nowrap">${esc(texto)}</span>
      </div>`;
  };

  const lineaNormal = ([c, v], padre = null) => {
    const sangria = padre !== null;
    const num = esNumero(v) ? aNumero(v) : null;
    const texto = num === null ? esc(v) : (esColumnaDePlata(c) ? fmtMoneda(num) : fmtNumero(num));
    // Debajo de "Ventas", el hijo se llama "efectivo" y no "Ventas efectivo": repetir el
    // padre en cada hijo es ruido, y la sangria ya dice de quien cuelga.
    const nombre = sangria
      ? etiquetaDe(String(c).toLowerCase().replace(new RegExp('^' + padre + '_'), ''))
      : etiquetaDe(c);
    return `
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding:${sangria ? '4px 0 4px 14px' : '6px 0'};border-bottom:1px solid #161b22">
        <span style="font-size:0.875rem;color:${sangria ? '#6e7681' : '#8b949e'}">${sangria ? '└ ' : ''}${esc(nombre)}</span>
        <span style="font-size:${sangria ? '0.875rem' : '0.9375rem'};color:#c9d1d9;font-variant-numeric:tabular-nums;white-space:nowrap">${esc(texto)}</span>
      </div>`;
  };

  // Un concepto destacado arrastra sus desgloses: "Efectivo" y "Transferencias" van
  // indentados bajo "Ventas". Al mismo nivel invitarian a sumarlos con el total.
  const conHijos = (linea, c) => {
    const padre = String(c).toLowerCase();
    const hijos = hijosDe.get(padre);
    return linea + (hijos ? hijos.map(h => lineaNormal(h, padre)).join('') : '');
  };

  // La letra chica va detras de un toggle: no estorba, pero sigue estando. Ahi vive el
  // "de donde sale esta cifra", que es lo que permite discutir un numero en vez de
  // creerlo o desconfiar de el.
  const pieHtml = pie.length === 0 ? '' : `
    <details style="margin-top:10px">
      <summary style="cursor:pointer;font-size:0.875rem;color:#58a6ff">De dónde sale cada cifra</summary>
      <div style="margin-top:6px">
        ${pie.map(([c, v]) => `
          <div style="padding:4px 0;font-size:0.875rem;color:#6e7681">
            <span style="color:#8b949e">${esc(etiquetaDe(c))}:</span> ${esc(v)}
          </div>`).join('')}
      </div>
    </details>`;

  // El "sin dato" se dice, no se esconde ni se disfraza de cero. Va discreto pero
  // presente: quien mira tiene que poder distinguir un cero real de una laguna.
  const sinDatoHtml = sinDato.length === 0 ? '' : `
    <div style="margin-top:9px;padding-top:7px;border-top:1px solid #21262d;font-size:0.875rem;color:#6e7681">
      Sin dato para este negocio: ${sinDato.map(c => esc(etiquetaDe(c))).join(', ')}.
      <span style="color:#8b949e">No es cero: este negocio no reporta ese campo.</span>
    </div>`;

  // UIA-T11 -- LA PUNTUALIDAD SE DICE CON LOS TRES NUMEROS, NUNCA CON EL PORCENTAJE SOLO.
  //
  // 4 de 4 da 100% y se lee como dia perfecto, pero si el negocio tiene 5 activos
  // significa que uno no marco. El caso extremo es peor: si marca UNA sola persona y
  // llega a tiempo, el porcentaje tambien da 100%. Un indicador que puede decir
  // "perfecto" cuando falto gente no es un indicador, es una trampa.
  //
  // Por eso el porcentaje va de apoyo y el numero completo manda. Las tres piezas
  // llegan por separado desde la capacidad (v3.3) justamente para poder decirlo asi.
  const iA = contexto.findIndex(([c]) => c === 'colaboradores_a_tiempo');
  const iM = contexto.findIndex(([c]) => c === 'colaboradores_marcaron');
  const iAct = contexto.findIndex(([c]) => c === 'colaboradores_activos');
  let puntualidadHtml = '';
  if (iA >= 0 && iM >= 0) {
    const aTiempo = aNumero(contexto[iA][1]);
    const marcaron = aNumero(contexto[iM][1]);
    const activos = iAct >= 0 ? aNumero(contexto[iAct][1]) : null;
    const pct = marcaron > 0 ? Math.round((aTiempo / marcaron) * 100) : null;

    // Si marcaron menos de los activos, eso se DICE. Es la parte que el porcentaje
    // esconde y la razon por la que Ares pidio el denominador explicito.
    const faltaron = activos !== null && activos > marcaron ? activos - marcaron : 0;
    const color = pct === null ? '#8b949e' : pct === 100 && faltaron === 0 ? '#3fb950' : pct >= 80 ? '#d29922' : '#f85149';

    puntualidadHtml = `
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;padding:6px 0;border-bottom:1px solid #161b22">
        <span style="font-size:0.875rem;color:#8b949e">Puntualidad</span>
        <span style="font-size:0.875rem;color:${color};white-space:nowrap">
          ${aTiempo} de ${marcaron} que marcaron${activos !== null ? ` <span style="color:#6e7681">(de ${activos} activos)</span>` : ''}
        </span>
      </div>
      ${(pct !== null || faltaron > 0) ? `<div style="font-size:0.875rem;color:#6e7681;padding:0 0 5px 0">
        ${pct !== null ? `${pct}% de los que marcaron` : 'Nadie marcó entrada'}${faltaron > 0 ? `${pct !== null ? ' · ' : ' · '}<span style="color:#d29922">${faltaron} ${faltaron === 1 ? 'no marcó' : 'no marcaron'}</span>` : ''}
      </div>` : ''}`;

    // Salen de la lista suelta: ya estan contados dentro de la frase de puntualidad.
    [iA, iM, iAct].filter(i => i >= 0).sort((a, b) => b - a).forEach(i => contexto.splice(i, 1));
  }

  // UIA-T13: el contexto va en su propio bloque al final. La diferencia del arqueo, las
  // facturas y la asistencia acompanan al informe pero no son plata del negocio: al
  // mismo nivel que ventas y gastos competian por la mirada con lo que si importa.
  const contextoHtml = (contexto.length === 0 && !puntualidadHtml) ? '' : `
    <div style="margin-top:10px;padding-top:8px;border-top:1px solid #21262d">
      <div style="font-size:0.875rem;color:#6e7681;margin-bottom:4px">Contexto del día</div>
      ${puntualidadHtml}
      ${contexto.map(f => lineaNormal(f)).join('')}
    </div>`;

  const tituloHtml = titulo ? `
    <div style="font-size:0.9rem;font-weight:600;color:#e6edf3;margin-bottom:8px;padding-bottom:7px;border-bottom:1px solid #30363d">${esc(titulo)}</div>` : '';

  return `
    <div style="border:1px solid #30363d;border-radius:8px;padding:12px 14px;margin:6px 0;background:#0d1117">
      ${tituloHtml}
      ${destacados.map(([c, v]) => conHijos(lineaDestacada([c, v]), c)).join('')}
      ${normales.filter(([c]) => !esHijo.has(c)).map(f => lineaNormal(f)).join('')}
      ${contextoHtml}
      ${sinDatoHtml}
      ${pieHtml}
    </div>`;
}
